import React, { useCallback } from 'react'
import { Spin } from 'antd'
import { DismissibleTooltip } from '../../common/DismissibleTooltip'
import { MessageCircle, Users } from 'lucide-react'
import styled from 'styled-components'
import {
  useStakeholderContext,
  useStakeholderTemplates,
  StakeholderAvatar,
} from '../../../features/stakeholder'
import { useOptionalChatWindow } from '../Sidebars/ChatWindow/useChatWindowContext'
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  TRANSITIONS,
  TYPOGRAPHY,
  FONT_WEIGHT,
} from '../../../styles/theme'
import type { StakeholderTemplate } from '@bandos/shared'
import type { ChatTarget } from '../../../context/ScopedChatContext'

// Styled Components
const DropdownContainer = styled.div`
  width: 320px;
  background: var(--color-bg-elevated);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
  max-height: calc(100dvh - 120px);
  display: flex;
  flex-direction: column;
`

const DropdownHeader = styled.div`
  padding: ${SPACING.sm} ${SPACING.md};
  border-bottom: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DropdownContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${SPACING.sm};
`

const SectionLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: ${SPACING.xs} ${SPACING.sm};
  margin-top: ${SPACING.sm};

  &:first-child {
    margin-top: 0;
  }
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StakeholderRow = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: ${SPACING.sm};
  background: ${(props) => (props.$isActive ? 'var(--color-bg-card)' : 'transparent')};
  border-radius: ${BORDER_RADIUS.md};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  gap: ${SPACING.sm};

  &:hover {
    background: var(--color-bg-hover);
  }
`

const RowContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const RowName = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowRole = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ChatButton = styled.button`
  width: 28px;
  height: 28px;
  min-width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${BORDER_RADIUS.full};
  border: none;
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  background: transparent;
  color: var(--color-text-muted);

  &:hover {
    background: ${COLORS.primary}15;
    color: ${COLORS.primary};
  }
`

const ManageTeamButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  background: ${COLORS.primary};
  border: none;
  border-radius: ${BORDER_RADIUS.md};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  color: white;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  margin-top: ${SPACING.sm};

  &:hover {
    background: ${COLORS.primaryHover};
  }
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.xl};
  gap: ${SPACING.md};
  color: var(--color-text-tertiary);
  text-align: center;
`

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${BORDER_RADIUS.full};
  background: var(--color-bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
`

const EmptyStateText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.4;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${SPACING.xl};
`

interface TeamDropdownProps {
  onClose: () => void
  onOpenCharacterModal: (selectedTemplateId?: string) => void
}

// Main Component
export function TeamDropdownContent({ onClose, onOpenCharacterModal }: TeamDropdownProps) {
  const { projectStakeholders } = useStakeholderContext()
  const chatWindow = useOptionalChatWindow()
  const { templates, loading, isGeneratingPortrait } = useStakeholderTemplates()

  const handleOpenChat = useCallback(
    (template: StakeholderTemplate, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!chatWindow) return

      const projectStakeholder = projectStakeholders.find(
        (ps) => ps.stakeholder_template_id === template.id
      )

      const target: ChatTarget = {
        type: 'stakeholder',
        id: projectStakeholder?.id,
        name: template.name,
        role: template.role ?? undefined,
      }

      chatWindow.openGeneralChat(target)
      onClose()
    },
    [chatWindow, projectStakeholders, onClose]
  )

  const handleManageTeam = useCallback(() => {
    onClose()
    onOpenCharacterModal()
  }, [onClose, onOpenCharacterModal])

  const handleRowClick = useCallback(
    (template: StakeholderTemplate) => {
      onClose()
      onOpenCharacterModal(template.id)
    },
    [onClose, onOpenCharacterModal]
  )

  if (loading) {
    return (
      <DropdownContainer>
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      </DropdownContainer>
    )
  }

  // Get templates for active team members
  const activeTemplates = projectStakeholders
    .map((ps) => templates.find((t) => t.id === ps.stakeholder_template_id))
    .filter((t): t is StakeholderTemplate => t !== undefined)

  const renderRow = (template: StakeholderTemplate) => {
    return (
      <StakeholderRow
        key={template.id}
        $isActive={true}
        onClick={() => handleRowClick(template)}
        style={{ cursor: 'pointer' }}
      >
        <StakeholderAvatar
          name={template.name}
          portraitUrl={template.portrait_url}
          isGenerating={isGeneratingPortrait(template.id)}
          size="sm"
        />
        <RowContent>
          <RowName>{template.name}</RowName>
          {template.role && <RowRole>{template.role}</RowRole>}
        </RowContent>
        <RowActions>
          {chatWindow && (
            <DismissibleTooltip
              title={`Chat with ${template.name}`}
              placement="top"
              mouseEnterDelay={0.5}
            >
              <ChatButton onClick={(e) => handleOpenChat(template, e)}>
                <MessageCircle size={14} />
              </ChatButton>
            </DismissibleTooltip>
          )}
        </RowActions>
      </StakeholderRow>
    )
  }

  return (
    <DropdownContainer>
      <DropdownHeader>Simulated Team</DropdownHeader>
      <DropdownContent>
        {activeTemplates.length > 0 ? (
          <>
            <SectionLabel>Team Members ({activeTemplates.length})</SectionLabel>
            <RowList>{activeTemplates.map((template) => renderRow(template))}</RowList>
          </>
        ) : (
          <EmptyStateContainer>
            <EmptyStateIcon>
              <Users size={24} />
            </EmptyStateIcon>
            <EmptyStateText>
              No team members yet.
              <br />
              Add stakeholders to get feedback on your ideas.
            </EmptyStateText>
          </EmptyStateContainer>
        )}
        <ManageTeamButton onClick={handleManageTeam}>
          <Users size={14} />
          Manage Team
        </ManageTeamButton>
      </DropdownContent>
    </DropdownContainer>
  )
}
